// 組織設定（portal.md「組織設定」）。admin は表示名と状態（有効 / 停止）を変えられ、正式名称は system-admin だけ。
// 契約 IDは変更不可。認証ソース（会社の SSO）は閲覧のみで、インフラの設定からデプロイ時に反映される（ADR-0018）。
// サービス利用はサービス利用画面（Subscriptions.tsx）。

import { useEffect, useState } from "react";
import { api, messageFor, type Mapping, type Contract, type ContractRows, type ContractStatus } from "../lib/api";
import { useContract } from "./ContractLayout";

export function ContractSettingsPage() {
  const { me, contractId } = useContract();
  const sysadmin = me.platformRole === "system-admin";
  const [rows, setRows] = useState<ContractRows | null>(null);
  const [mappings, setMappings] = useState<Mapping[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    try {
      const [t, m] = await Promise.all([api.contract(contractId), api.mappings(contractId)]);
      setRows(t); setMappings(m.mappings);
    } catch (e) { setError(messageFor(e)); }
  };
  useEffect(() => { void load(); }, [contractId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rows || !mappings) return <main>{error ? <p className="error">{error}</p> : <p className="muted">読み込み中…</p>}</main>;

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setError(null); setNotice(null);
    try { await fn(); await load(); setNotice(done); } catch (e) { setError(messageFor(e)); }
  };

  return (
    <main>
      <h1>組織設定</h1>
      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      <ContractForm
        key={JSON.stringify(rows.contract)} contract={rows.contract} canSetLegalName={sysadmin}
        onSave={(patch) => act(() => api.updateContract(contractId, patch), "組織設定を保存しました。")}
      />
      <h2>ログイン方法（会社の SSO）</h2>
      <MappingTable mappings={mappings} />
    </main>
  );
}

function ContractForm({ contract, canSetLegalName, onSave }: {
  contract: Contract; canSetLegalName: boolean;
  onSave: (patch: { name: string; legalName?: string; status: ContractStatus }) => void;
}) {
  const [name, setName] = useState(contract.name);
  const [legalName, setLegalName] = useState(contract.legalName ?? "");
  const [status, setStatus] = useState(contract.status);
  const legalOk = !canSetLegalName || !!legalName.trim();
  return (
    <div className="card">
      <label>契約 ID</label>
      <input type="text" value={contract.contractId} disabled />
      <label htmlFor="legal-name">正式名称</label>
      <input id="legal-name" type="text" value={legalName} disabled={!canSetLegalName}
        placeholder={canSetLegalName ? "株式会社〇〇" : "未登録"} onChange={(e) => setLegalName(e.target.value)} />
      <label htmlFor="contract-name">表示名</label>
      <input id="contract-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
      <label>状態</label>
      <select style={{ width: 280 }} value={status} onChange={(e) => setStatus(e.target.value as ContractStatus)}>
        <option value="active">有効</option>
        <option value="suspended">停止</option>
      </select>
      {status === "suspended" && (
        <p className="note"><span aria-hidden>ⓘ</span><span>停止は新しい招待の発行と、受諾前の招待リンクからの登録ができなくなります。既存ユーザーのログインとサービス利用は停止できません（止めるには契約を無効にします）。</span></p>
      )}
      <p><button className="primary" disabled={!name.trim() || !legalOk}
        onClick={() => onSave({ name: name.trim(), status, ...(canSetLegalName ? { legalName: legalName.trim() } : {}) })}>保存</button></p>
    </div>
  );
}

/** 認証ソースマッピングの閲覧。登録・変更はインフラの設定（lib/project.ts の samlIdps）とデプロイで行う。 */
function MappingTable({ mappings }: { mappings: Mapping[] }) {
  return (
    <div className="card">
      <p className="muted">
        下のドメインのメールアドレスの人は、ログイン画面で会社の IdP へ振り分けられ、パスワードではログインできません。
        それ以外のドメインの人はメールアドレスとパスワードでログインします。
        IdP とドメインの追加・変更はシステム管理者が行います（インフラの設定とデプロイで反映）。
      </p>
      <table>
        <thead><tr><th>IdP</th><th>メールドメイン</th><th>プール</th></tr></thead>
        <tbody>
          {mappings.length === 0 && <tr><td colSpan={3} className="muted">会社の SSO は登録されていません（全員がメールアドレスとパスワードでログインします）。</td></tr>}
          {mappings.map((m) => (
            <tr key={`${m.iss}#${m.providerName}#${m.emailDomain}`}>
              <td>{m.providerName || "（プール内のすべての IdP）"}</td>
              <td>{m.emailDomain || "（すべてのドメイン）"}</td>
              <td className="muted">{m.poolKind === "shared" ? "共有プール" : m.poolKind === "dedicated" ? "会社専用プール" : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
